import { components } from "@/api-schema";
import { useDepartmentSearchQuery } from "@/common/api/department/use-department-search.query";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { BaseFilterProps, Filter, FilterData, FilterPaginationState } from "../_types";
import { FilterComponent } from "../base-filter";

export type QueryBody = components["schemas"]["DepartmentSearchBody"];

const FILTER_PAGE_SIZE = 50;

// Custom hook to fetch department data with pagination
function useDepartmentFilterData(body: QueryBody): { data: FilterData; pagination: FilterPaginationState } {
  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination({
    initialPageSize: FILTER_PAGE_SIZE,
  });

  const {
    data: departments,
    isPending: isLoading,
    error,
  } = useDepartmentSearchQuery({
    options: {
      parameters: {
        pageSize,
        search: debouncedSearchTerm || undefined,
      },
      body,
    },
  });

  const options =
    departments?.data
      .map((element) => ({
        id: element.uuid ?? "",
        name: element.name ?? "",
      }))
      .filter((option) => option.id && option.name)
      .filter((option, index, array) => array.findIndex((item) => item.id === option.id) === index) || [];

  const loadedCount = departments?.data?.length ?? 0;
  const total = departments?.meta?.total ?? 0;

  const pagination = createPaginationState(total, loadedCount, isLoading);

  return {
    data: {
      options,
      isLoading,
      error,
    },
    pagination,
  };
}

export class DepartmentFilter extends Filter<QueryBody, unknown> {
  readonly id: string = "departments";
  readonly labelSingular = "common:Department";
  readonly labelPlural = "ram:Departments";
  readonly placeholder = "ram:SelectDepartments";
  readonly searchPlaceholder = "common:SearchDepartments";
  readonly emptyText = "common:NoDepartmentsFound";

  constructor({ id, body = {} }: { id?: string; body?: Partial<QueryBody> } = {}) {
    super();
    if (id) this.id = id;
    this.body = body as QueryBody;
  }

  getData(): FilterData {
    return { options: [], isLoading: false, error: null };
  }

  render(props: BaseFilterProps) {
    return <DepartmentFilterComponent filter={this} {...props} />;
  }
}

function DepartmentFilterComponent({ filter, ...props }: { filter: DepartmentFilter } & BaseFilterProps) {
  const { data, pagination } = useDepartmentFilterData(filter.body);

  const filterWithData = Object.create(filter);
  filterWithData.getData = () => data;

  return <FilterComponent filter={filterWithData} pagination={pagination} {...props} />;
}
